// Seasonal pricing for the five venue packages.
//
// Rates are net venue price: they exclude WA sales tax and the refundable
// security deposit. 2028 is 2027 + 3%, rounded to the nearest $100.

export type Tier = "peak" | "shoulder" | "off";
export type DayType = "weekend" | "weekday";
export type PackageId =
  | "hummingbird"
  | "osprey"
  | "blueHeron"
  | "silkieChicken"
  | "peacock";

export const PRICED_YEARS = [2026, 2027, 2028] as const;
export type PricedYear = (typeof PRICED_YEARS)[number];

export const TIER_LABELS: Record<Tier, string> = {
  peak: "Peak season",
  shoulder: "Shoulder season",
  off: "Off season",
};

export const TIER_MONTHS: Record<Tier, string> = {
  peak: "June – October",
  shoulder: "April – May",
  off: "March & November",
};

// [weekend, weekday] per tier.
type TierRates = Record<Tier, readonly [number, number]>;

const RATES: Record<PricedYear, Record<PackageId, TierRates>> = {
  // 2026 predates the seasonal structure — one flat rate, repeated across
  // tiers so existing 2026 enquiries still quote correctly.
  2026: {
    hummingbird: { peak: [5700, 2400], shoulder: [5700, 2400], off: [5700, 2400] },
    osprey: { peak: [6800, 3400], shoulder: [6800, 3400], off: [6800, 3400] },
    blueHeron: { peak: [7300, 3500], shoulder: [7300, 3500], off: [7300, 3500] },
    silkieChicken: { peak: [8700, 4900], shoulder: [8700, 4900], off: [8700, 4900] },
    peacock: { peak: [9500, 5700], shoulder: [9500, 5700], off: [9500, 5700] },
  },
  2027: {
    hummingbird: { peak: [6300, 3000], shoulder: [5500, 2600], off: [4300, 2400] },
    osprey: { peak: [7400, 4000], shoulder: [6500, 3600], off: [5100, 3400] },
    blueHeron: { peak: [7600, 4200], shoulder: [7000, 3900], off: [5500, 3600] },
    silkieChicken: { peak: [9100, 5100], shoulder: [8300, 4600], off: [6500, 4600] },
    peacock: { peak: [10100, 5600], shoulder: [9200, 5100], off: [7200, 5100] },
  },
  2028: {
    hummingbird: { peak: [6500, 3100], shoulder: [5700, 2700], off: [4400, 2500] },
    osprey: { peak: [7600, 4100], shoulder: [6700, 3700], off: [5300, 3500] },
    blueHeron: { peak: [7800, 4300], shoulder: [7200, 4000], off: [5700, 3700] },
    silkieChicken: { peak: [9400, 5300], shoulder: [8500, 4700], off: [6700, 4700] },
    peacock: { peak: [10400, 5800], shoulder: [9500, 5300], off: [7400, 5300] },
  },
};

// Additional nights are 30% off the Hummingbird rate for the day the extra
// night itself falls on — not the day of the event. A Friday night before a
// Saturday wedding bills at the Fri–Sun rate.
export const ADDITIONAL_NIGHT_DISCOUNT = 0.3;

/** Washington state sales tax, applied to the venue rate. */
export const TAX_RATE = 0.099;

/** Collected at booking, returned after the event. */
export const SECURITY_DEPOSIT = 1000;

export const additionalNightRate = (
  year: PricedYear,
  tier: Tier,
  dayType: DayType,
) => {
  const [weekend, weekday] = RATES[year].hummingbird[tier];
  const base = dayType === "weekend" ? weekend : weekday;
  return Math.round(base * (1 - ADDITIONAL_NIGHT_DISCOUNT));
};

export const priceFor = (
  pkg: PackageId,
  year: PricedYear,
  tier: Tier,
  dayType: DayType,
) => {
  const [weekend, weekday] = RATES[year][pkg][tier];
  return dayType === "weekend" ? weekend : weekday;
};

export const formatPrice = (amount: number) => `$${amount.toLocaleString()}`;

// ── Date → season ─────────────────────────────────────────────────────────

// Closed to events Dec 1 – Feb 28/29.
export const CLOSED_MONTHS = [12, 1, 2];

export const tierForMonth = (month: number): Tier | null => {
  if (CLOSED_MONTHS.includes(month)) return null;
  if (month >= 6 && month <= 10) return "peak";
  if (month === 4 || month === 5) return "shoulder";
  return "off"; // March, November
};

// Friday, Saturday and Sunday all bill at the weekend rate.
export const dayTypeForDate = (date: Date): DayType => {
  const day = date.getDay();
  return day === 5 || day === 6 || day === 0 ? "weekend" : "weekday";
};

export const isPricedYear = (year: number): year is PricedYear =>
  (PRICED_YEARS as readonly number[]).includes(year);

export interface DateContext {
  year: PricedYear;
  tier: Tier;
  dayType: DayType;
}

export type DateResolution =
  | { status: "ok"; context: DateContext }
  | { status: "closed" }
  | { status: "past" }
  | { status: "unpriced" };

/** Resolves a chosen date into the pricing context used across the page. */
export const resolveDate = (date: Date, today: Date): DateResolution => {
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  if (date < startOfToday) return { status: "past" };

  const tier = tierForMonth(date.getMonth() + 1);
  if (!tier) return { status: "closed" };

  const year = date.getFullYear();
  if (!isPricedYear(year)) return { status: "unpriced" };

  return {
    status: "ok",
    context: { year, tier, dayType: dayTypeForDate(date) },
  };
};

export const formatShortDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const formatLongDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

// ── Default selection ─────────────────────────────────────────────────────
// The page opens on a peak-season weekend date that is still available, so the
// first price a visitor sees is one they could actually book.
//
// NOTE: both of these are tied to 2027 and need bumping for the 2028 season —
// once 2027's peak is in the past, every candidate is filtered out and the
// fallback is a past date, which leaves the page with no prices showing.
export const DEFAULT_YEAR: PricedYear = 2027;
export const FALLBACK_DATE = new Date(2027, 7, 14); // Sat, Aug 14 2027

const PEAK_FIRST_MONTH = 6;
const PEAK_LAST_MONTH = 10;

/** Midpoint of peak season — Aug 16 in 2027. */
const peakMidpoint = (year: number) => {
  const start = new Date(year, PEAK_FIRST_MONTH - 1, 1);
  const end = new Date(year, PEAK_LAST_MONTH, 0); // last day of October
  return new Date((start.getTime() + end.getTime()) / 2);
};

/**
 * The weekend date (Fri/Sat/Sun — all bill at the weekend rate) nearest the
 * middle of peak season that is neither booked nor in the past. Falls back to
 * FALLBACK_DATE when every candidate is taken.
 */
export const pickDefaultDate = (
  isBooked: (date: Date) => boolean,
  today: Date,
  year: PricedYear = DEFAULT_YEAR,
): Date => {
  const midpoint = peakMidpoint(year).getTime();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const candidates: Date[] = [];
  const cursor = new Date(year, PEAK_FIRST_MONTH - 1, 1);
  const lastDay = new Date(year, PEAK_LAST_MONTH, 0);
  while (cursor <= lastDay) {
    const date = new Date(cursor);
    if (dayTypeForDate(date) === "weekend" && date >= startOfToday) {
      candidates.push(date);
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  candidates.sort(
    (a, b) =>
      Math.abs(a.getTime() - midpoint) - Math.abs(b.getTime() - midpoint) ||
      a.getTime() - b.getTime(),
  );

  return candidates.find((date) => !isBooked(date)) ?? FALLBACK_DATE;
};
